import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { navigationItems } from '../data/navigation';
import LanguageSwitcher from './LanguageSwitcher';
import './Header.css';

const Header: React.FC = () => {
  const { t } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Close mobile menu when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };

    // Only add event listeners when mobile menu is open
    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    // Clean up event listeners
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isMobileMenuOpen]);

  return (
    <header className="header" ref={headerRef}>
      <div className="header-container">
        {/* Logo Section */}
        <div className="logo-section">
          <a href="/" className="logo-link">
            <img 
              src="/Peja Peace Forum Logo.svg" 
              alt="Peja Peace Forum" 
              className="logo-image"
              onError={(e) => {
                // Fallback if logo image doesn't exist
                e.currentTarget.style.display = 'none';
                const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                if (nextElement) {
                  nextElement.style.display = 'block';
                }
              }}
            />
            <h1 className="logo-text" style={{ display: 'none' }}>
              Peja Peace Forum
            </h1>
          </a>
        </div>

        {/* Desktop Navigation */}
        <nav className="desktop-nav">
          <ul className="nav-list">
            {navigationItems.map((item) => (
              <li 
                key={item.id} 
                className={`nav-item ${item.children ? 'has-dropdown' : ''}`}
                onMouseEnter={() => item.children && setActiveDropdown(item.id)}
                onMouseLeave={() => item.children && setActiveDropdown(null)}
              >
                <a href={item.href} className="nav-link">
                  {t(`navigation.${item.id}`)}
                  {item.children && <span className="dropdown-arrow">▼</span>}
                </a>
                
                {item.children && activeDropdown === item.id && (
                  <ul className="dropdown-menu">
                    {item.children.map((child) => (
                      <li key={child.id} className="dropdown-item">
                        <a href={child.href} className="dropdown-link">
                          {t(`navigation.${child.id}`)}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </nav>

        {/* Language Switcher */}
        <LanguageSwitcher />

        {/* Spacer for mobile menu button */}
        <div className="header-spacer"></div>

        {/* Mobile Menu Button */}
        <button 
          className="mobile-menu-button"
          onClick={toggleMobileMenu}
          aria-label="Toggle mobile menu"
        >
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </button>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <nav className="mobile-nav">
          <ul className="mobile-nav-list">
            {navigationItems.map((item) => (
              <li key={item.id} className="mobile-nav-item">
                <a href={item.href} className="mobile-nav-link" onClick={closeMobileMenu}>
                  {t(`navigation.${item.id}`)}
                </a>
                {item.children && (
                  <ul className="mobile-dropdown">
                    {item.children.map((child) => (
                      <li key={child.id} className="mobile-dropdown-item">
                        <a href={child.href} className="mobile-dropdown-link" onClick={closeMobileMenu}>
                          {t(`navigation.${child.id}`)}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </nav>
      )}
    </header>
  );
};

export default Header;
